import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown,
  Server,
  Database,
  Zap,
  Clock,
  Users,
  RefreshCw,
  Wrench
} from 'lucide-react';
import { superadminApi } from '../../utils/api';
import { toast } from 'sonner';

interface SystemMetrics {
  apiResponseTime: number;
  errorRate: number;
  uptime: number;
  activeUsers: number;
  requestsPerMinute: number;
  databaseConnections: number;
  cacheHitRate: number;
  storageUsed: number;
  storageTotal: number;
}

interface HealthCheck {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
  lastChecked: string;
  message?: string;
}

export default function SystemHealthMonitor() {
  const [metrics, setMetrics] = useState<SystemMetrics>({
    apiResponseTime: 0,
    errorRate: 0,
    uptime: 99.99,
    activeUsers: 0,
    requestsPerMinute: 0,
    databaseConnections: 0,
    cacheHitRate: 0,
    storageUsed: 0,
    storageTotal: 0,
  });
  
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [fixingMappings, setFixingMappings] = useState(false);

  useEffect(() => {
    loadSystemMetrics();
    // Refresh every 30 seconds
    const interval = setInterval(loadSystemMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemMetrics = async () => {
    try {
      setLoading(true);
      
      // Load system metrics from backend
      const data = await superadminApi.get('/system-metrics');
      
      if (data) {
        setMetrics(data.metrics || metrics);
        setHealthChecks(data.healthChecks || []);
      } else {
        // Mock data for demo
        setMetrics({
          apiResponseTime: Math.floor(Math.random() * 150) + 50,
          errorRate: Math.random() * 0.5,
          uptime: 99.99,
          activeUsers: Math.floor(Math.random() * 50) + 20,
          requestsPerMinute: Math.floor(Math.random() * 500) + 200,
          databaseConnections: Math.floor(Math.random() * 20) + 5,
          cacheHitRate: 92 + Math.random() * 5,
          storageUsed: 45.6,
          storageTotal: 100,
        });
        
        setHealthChecks([
          {
            service: 'API Server',
            status: 'healthy',
            responseTime: 45,
            lastChecked: new Date().toISOString(),
          },
          {
            service: 'Database',
            status: 'healthy',
            responseTime: 12,
            lastChecked: new Date().toISOString(),
          },
          {
            service: 'Storage Service',
            status: 'healthy',
            responseTime: 89,
            lastChecked: new Date().toISOString(),
          },
          {
            service: 'Cache Server',
            status: 'healthy',
            responseTime: 3,
            lastChecked: new Date().toISOString(),
          },
        ]);
      }
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading system metrics:', error);
      toast.error('Failed to load system metrics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'down': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'degraded': return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'down': return <AlertCircle className="h-5 w-5 text-red-600" />;
      default: return <Activity className="h-5 w-5 text-gray-600" />;
    }
  };

  const fixUserLoginMappings = async () => {
    try {
      setFixingMappings(true);
      toast.loading('Fixing user login mappings...', { id: 'fix-mappings' });
      
      const result = await superadminApi.post('/fix-user-mappings', {});
      
      if (result && result.success) {
        toast.success(
          `Fixed ${result.totalFixed} users out of ${result.totalChecked} checked`,
          { id: 'fix-mappings' }
        );
      } else {
        toast.error('Failed to fix user mappings', { id: 'fix-mappings' });
      }
    } catch (error) {
      console.error('Error fixing user mappings:', error);
      toast.error('Error fixing user mappings', { id: 'fix-mappings' });
    } finally {
      setFixingMappings(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl flex items-center gap-2">
            <Activity className="h-6 w-6 text-blue-600" />
            System Health Monitor
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <Button
          onClick={loadSystemMetrics}
          variant="outline"
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Zap className="h-4 w-4 text-blue-600" />
              API Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{metrics.apiResponseTime}</span>
              <span className="text-gray-500">ms</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              {metrics.apiResponseTime < 100 ? (
                <>
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">Excellent</span>
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm text-yellow-600">Normal</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <AlertCircle className="h-4 w-4 text-red-600" />
              Error Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{metrics.errorRate.toFixed(2)}</span>
              <span className="text-gray-500">%</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              {metrics.errorRate < 1 ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">Healthy</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">Action Needed</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Clock className="h-4 w-4 text-green-600" />
              Uptime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{metrics.uptime}</span>
              <span className="text-gray-500">%</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-600">99.99% SLA</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Users className="h-4 w-4 text-purple-600" />
              Active Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{metrics.activeUsers}</span>
              <span className="text-gray-500">online</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              <Activity className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-purple-600">Real-time</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Health Checks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-600" />
            Service Health Checks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {healthChecks.map((check, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(check.status)}`}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(check.status)}
                  <div>
                    <p className="font-medium">{check.service}</p>
                    <p className="text-sm opacity-75">
                      Response time: {check.responseTime}ms
                    </p>
                  </div>
                </div>
                <Badge className={getStatusColor(check.status)}>
                  {check.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Request Throughput
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Requests/min</span>
                <span className="font-medium">{metrics.requestsPerMinute}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Cache Hit Rate</span>
                <span className="font-medium">{metrics.cacheHitRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">DB Connections</span>
                <span className="font-medium">{metrics.databaseConnections}/50</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-600" />
              Storage Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Used Storage</span>
                  <span className="font-medium">
                    {metrics.storageUsed.toFixed(1)} / {metrics.storageTotal} GB
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      metrics.storageUsed / metrics.storageTotal > 0.8
                        ? 'bg-red-600'
                        : 'bg-blue-600'
                    }`}
                    style={{ width: `${(metrics.storageUsed / metrics.storageTotal) * 100}%` }}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {((metrics.storageUsed / metrics.storageTotal) * 100).toFixed(1)}% utilized
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            System Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.errorRate > 1 || metrics.storageUsed / metrics.storageTotal > 0.8 ? (
            <div className="space-y-2">
              {metrics.errorRate > 1 && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">High Error Rate Detected</p>
                    <p className="text-sm text-red-700 mt-1">
                      Current error rate ({metrics.errorRate.toFixed(2)}%) exceeds threshold. Investigate immediately.
                    </p>
                  </div>
                </div>
              )}
              {metrics.storageUsed / metrics.storageTotal > 0.8 && (
                <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900">Storage Capacity Warning</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Storage is at {((metrics.storageUsed / metrics.storageTotal) * 100).toFixed(1)}% capacity. Consider scaling up.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">All Systems Operational</p>
                <p className="text-sm text-green-700 mt-1">
                  No active alerts. All services are running normally.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Maintenance Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-blue-600" />
            Database Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Wrench className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Fix User Login Mappings</p>
                <p className="text-sm text-blue-700 mt-1">
                  Scans all tenants and adds missing email-to-userId mappings for existing users.
                  Run this if users are unable to login even with correct credentials.
                </p>
                <Button
                  onClick={fixUserLoginMappings}
                  disabled={fixingMappings}
                  className="mt-3 bg-blue-600 hover:bg-blue-700"
                >
                  {fixingMappings ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Fixing Mappings...
                    </>
                  ) : (
                    <>
                      <Wrench className="h-4 w-4 mr-2" />
                      Run Fix Now
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}